<template>
  <div class="ai-tags">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t('__aiTagManagement') }}</h2>

      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap">
        <form @submit.prevent="searchTags" class="me-auto me-2 mb-2">
          <div class="input-group rounded-pill">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control rounded-pill"
              :placeholder="$t('__searchAiTag')"
              aria-label="search"
              aria-describedby="search"
            />
            <button
              type="submit"
              class="btn btn-link px-2 text-dark"
              id="search"
            >
              <i class="bi bi-search"></i>
            </button>
            <button
              v-if="searchQuery"
              type="button"
              class="btn btn-primary px-3 ms-1 rounded-pill"
              @click="clearSearch"
            >
              {{ $t("__clearSearch") }}
            </button>
          </div>
        </form>

        <button
          @click="showModal()"
          class="mb-2 fs-5 btn btn-primary px-2 rounded-circle lh-sm"
        >
          <i class="bi bi-plus"></i>
        </button>
      </div>

      <div class="card">
        <div v-if="loading" class="card-body text-center">
          <i class="fas fa-spinner fa-spin"></i> {{ $t('__loading') }}
        </div>
        <div v-else-if="tags && tags.length > 0" class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="tags"
            :fields="fields"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>

            <template v-slot:cell(tag_name)="data">
              <span class="badge bg-primary">{{ data.item.tag_name }}</span>
            </template>

            <template v-slot:cell(category)="data">
              {{ data.item.category || '-' }}
            </template>

            <template v-slot:cell(usage_count)="data">
              <span class="badge bg-info">{{ data.item.usage_count || 0 }}</span>
            </template>

            <template v-slot:cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>

            <template v-slot:cell(actions)="data">
              <div class="d-flex">
                <button
                  @click="showModal(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  @click="confirmDelete(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  :disabled="data.item.usage_count > 0"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="data.item.usage_count > 0 ? $t('__aiTagCannotDelete') : $t('__delete')"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </template>
          </b-table>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="center"
            @change="onPageChange"
          ></b-pagination>
        </div>
        <div v-else class="card-body text-center text-muted">
          {{ $t('__noAiTagData') }}
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="tagModal" ref="tagModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="saveTag">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ form.id ? $t('__editAiTag') : $t('__addAiTag') }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label for="tag_name" class="form-label fw-medium required">{{ $t('__aiTagName') }}</label>
                <input
                  type="text"
                  v-model="form.tag_name"
                  class="form-control"
                  id="tag_name"
                  maxlength="50"
                  required
                >
              </div>
            </div>
            <div class="modal-footer justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-pill"
                data-bs-dismiss="modal"
              >
                {{ $t('__cancel') }}
              </button>
              <button type="submit" class="btn btn-primary rounded-pill">
                {{ $t('__save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aiKnowledge } from '../http/api/aiKnowledge'
import { mapMutations } from 'vuex'
import i18n from '@/lang/lang.js'
import { Modal } from 'bootstrap'

export default {
  name: 'AiTags',
  data() {
    return {
      loading: false,
      tags: [],
      searchQuery: '',
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,
      perPage: 20,
      fields: [
        { key: 'tag_name', label: this.$t('__aiTagName') },
        { key: 'usage_count', label: this.$t('__aiTagUsageCount'), thStyle: { width: '100px' } },
        { key: 'created_at', label: this.$t('__createdAt'), thStyle: { width: '150px' } },
        { key: 'actions', label: this.$t('__actions'), thStyle: { width: '100px' } }
      ],
      form: {
        id: null,
        tag_name: ''
      },
      bootstrap_modal: null
    }
  },
  computed: {
    displayPages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    }
  },
  mounted() {
    this.loadTags()
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),

    loadTags() {
      const vm = this
      vm.loading = true
      vm.Loading()
      const token = localStorage.getItem('humetrics_token')
      const params = {
        page: vm.currentPage,
        per_page: vm.perPage
      }

      if (vm.searchQuery) {
        params.search = vm.searchQuery
      }


      aiKnowledge.tags.get(params, token).then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          vm.tags = res.data.data || []
          // 不要覆蓋 currentPage，只在需要時更新
          vm.totalRows = res.data.total || 0
          vm.totalPages = res.data.last_page || 1
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: '載入標籤失敗'
          })
          vm.Alerted()
        }
        vm.loading = false
        vm.Loaded()
      }).catch((error) => {
        console.error('載入標籤失敗:', error)
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: '載入標籤失敗'
        })
        vm.Alerted()
        vm.loading = false
        vm.Loaded()
      })
    },

    onPageChange(page) {
      console.log('切換到頁面:', page)
      this.currentPage = page
      this.loadTags()
    },

    searchTags() {
      this.currentPage = 1
      this.loadTags()
    },

    clearSearch() {
      this.searchQuery = ''
      this.currentPage = 1
      this.loadTags()
    },

    showModal(id = null) {
      const vm = this
      if (id) {
        const tag = vm.tags.find(t => t.id === id)
        if (tag) {
          vm.form = { ...tag }
        }
      } else {
        vm.form = {
          id: null,
          tag_name: ''
        }
      }
      vm.bootstrap_modal = new Modal(vm.$refs.tagModal)
      vm.$nextTick(function () {
        vm.bootstrap_modal.show()
      })
    },

    saveTag(e) {
      if (e) {
        e.preventDefault()
      }

      const vm = this
      const token = localStorage.getItem('humetrics_token')

      if (!vm.form.tag_name) {
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: '請輸入標籤名稱'
        })
        vm.Alerted()
        return
      }

      vm.Loading()
      const apiCall = vm.form.id
        ? aiKnowledge.tags.update(vm.form.id, vm.form, token)
        : aiKnowledge.tags.create(vm.form, token)

      apiCall.then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          vm.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: vm.form.id ? i18n.t('__editSuccess') : i18n.t('__createSuccess')
          })
          vm.Alerted()
          vm.bootstrap_modal.hide()
          vm.loadTags()
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: res.data.message || '操作失敗'
          })
          vm.Alerted()
        }
        vm.Loaded()
      }).catch((error) => {
        console.error('儲存失敗:', error)
        if (error.response && error.response.data && error.response.data.errors) {
          const firstError = Object.values(error.response.data.errors)[0][0]
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: firstError
          })
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response?.data?.message || '儲存失敗'
          })
        }
        vm.Alerted()
        vm.Loaded()
      })
    },

    confirmDelete(tag) {
      const vm = this
      const token = localStorage.getItem('humetrics_token')

      if (tag.usage_count > 0) {
        vm.AlertsInfo({
          state: 'warning',
          title: '無法刪除',
          info: '使用中的標籤無法刪除'
        })
        vm.Alerted()
        return
      }

      if (confirm(this.$t('__deleteAiTagNote', { name: tag.tag_name }))) {
        vm.Loading()
        aiKnowledge.tags.delete(tag.id, token).then((res) => {
          if (res.status <= 201 && res.data.status === 'success') {
            vm.AlertsInfo({
              state: 'success',
              title: i18n.t('__success'),
              info: i18n.t('__deleteSuccess')
            })
            vm.Alerted()
            vm.loadTags()
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: res.data.message || '刪除失敗'
            })
            vm.Alerted()
          }
          vm.Loaded()
        }).catch((error) => {
          console.error('刪除失敗:', error)
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response?.data?.message || '刪除失敗'
          })
          vm.Alerted()
          vm.Loaded()
        })
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.device-report-select select {
  min-width: 150px;
}

.badge {
  font-size: 0.875rem;
}

.form-label.required:after {
  content: " *";
  color: #dc3545;
}
</style>
